export default {
  id: "calendar",
  title: "Calendar & Scheduling",
  section: "Daily Operations",
  order: 20,
  keywords: "calendar schedule recurring shifts day week month team view availability group virtual create",
  body: `
## What it is

The Calendar is the scheduling surface: shifts laid out in Day, Week,
Month, Team, or a custom date range, with filters for status, caregiver,
client, payer, type, agency, and admin. Click any shift for details and
actions (clock in/out, edit, approve); the **+** next to the page title
creates a new shift.

## The views

- **Day / Week** — time grids. On phones they scroll sideways with the
  time column pinned.
- **Month** — the overview. Hovering a shift shows a tooltip with times,
  hours, code, status, notes, and recorded miles.
- **Team** — one row per caregiver side by side, including their
  availability windows. The fastest way to find coverage.
- **Custom Range** — any span; longer ranges render like a month.
- Caregivers see only their own calendar (with a total-miles chip for the
  visible period). The color legend for status/type/payer sits above the
  grid.

## Step by step: creating a shift

1. Click **+**, choose **Single Shift** or **Recurring Shift**.
2. Pick the **Payer** — VR, DSPD, or Non Service — and for DSPD/Private
   the **Shift Type** (1-on-1 or Group). The form adapts:
   - **VR**: one client, one or more service codes, one or more caregivers.
   - **DSPD 1-on-1**: client, one service code, one caregiver.
   - **Group**: pick the service code first — then the client list offers
     only clients holding an **active authorization for that code** — plus
     a group name and multiple caregivers.
   - **Non Service**: a non-service code (Training, Admin, Meeting...) and
     caregivers, no client.
3. Set the **date and times** (recurring: start date, optional end date,
   times, and the weekdays). Note the built-in duration rules: DSP shifts
   must be 3–6 hours, DSG 6–10.
4. **Location**: the client's address is used for the shift (and for
   clock-in distance checks); tick **Use Alternate Location** for a
   different address, **Virtual Shift** for remote (paste the meeting
   link), or separate clock-in/out addresses when they differ. If an
   address has no map point yet, use **Generate Geocode**.
5. Optionally add **Shadow Caregivers** (single shifts only — they're
   per-occurrence, not per-series).
6. Save. If the shift would exceed the client's remaining authorization,
   acknowledge the warning checkbox first.

**Caregiver conflicts are filtered, with a deliberate way through**: the
caregiver dropdown only offers caregivers whose work-schedule windows cover
the time and who have no overlapping shift or approved time off. When you
need someone the filter hides, tick **Show all caregivers** — everyone
appears, each labelled with why they'd normally be hidden ("already booked
on an overlapping shift", "not scheduled to work Tuesdays", "approved time
off"). Picking one is allowed, but saving asks you to confirm the
consequence first, because these land outside the schedule:

- **Already booked** — payroll counts the hours from both shifts, so their
  period total will read higher than they could actually have worked and
  needs a manual correction before payroll is approved.
- **Outside their schedule** — the shift won't line up with their
  availability in reports and may push them into overtime.
- **Approved time off** — the request stays approved, so they may not show
  up; the shift then needs reassigning or the time off revisiting.

Confirming records a "Caregiver Availability Overridden" entry on the
shift's history and alerts admins and the caregiver's lead. Unticking the
box clears any selection that only appeared because of it. Inactive staff
and ABI/TBI certification requirements are never bypassed by this.

**Client overlaps can be scheduled anyway**: if a client already has a
shift in the window (common for group day shifts where one client is
dropped off at work partway through), saving shows the conflict and asks
"Schedule anyway?". Confirming saves the shift, records the acknowledgment
in the shift's history, and sends a warning notification to admins and the
caregivers' leads — so double-bookings are always deliberate and visible.
This works for recurring series too, not just single shifts.

Note there is no equivalent "show all clients" override: a client missing
from the dropdown is missing because they're inactive, their payer type
doesn't match, or they have no active authorization for the service code —
and scheduling against a missing authorization produces a shift that can't
be billed. Fix the authorization rather than working around the list.

**Group shifts** continue to two more screens: per-client address
selection, then per-client task assignment — which caregiver handles Pick
Up, Drop Off, Shift Notes, and (if the client has them) Medications.

## Step by step: editing a recurring series

1. Open a future occurrence and edit.
2. Choose **Single Shift** to change only that occurrence (it becomes an
   exception) or **Recurring Shift** to change the series.
3. Series edits show a confirmation: the current series **ends** at the
   cutoff date and a **new series** starts with the new details — past
   occurrences never change. If exceptions exist in the affected range,
   you pick which move to the new series and which are deleted.
4. Deleting a recurring shift asks the scope: **this shift only**, **this
   and future**, or **the entire series**.

## Shadow & training caregivers

One caregiver is the **primary**; others can join in a shadow or training
role. They clock independently and are paid, but never appear on client
documentation or billing. Calendars show everyone assigned.

## Detail-page calendars

Each caregiver's and client's Details page has its own month calendar with
a weekly summary column — scheduled vs. approved hours by code, plus the
week's recorded miles.
`,
};
